import * as THREE from 'three';
import type { Input } from '../core/Input';
import type { AudioManager } from '../audio/AudioManager';
import type { PlayerController } from '../player/PlayerController';

const INTERACT_DIST = 1.9;
const FACING_DOT = 0.12;

export interface AlarmButton {
  root: THREE.Object3D;
  stand: THREE.Vector3;
  knob: THREE.Mesh;
}

/** Wall alarm panels — F to pull, or a guard can sprint over and hit one. */
export class AlarmSystem {
  readonly buttons: AlarmButton[] = [];
  sounded = false;
  onSounded: (() => void) | null = null;
  private readonly lamps: THREE.Mesh[] = [];
  private readonly lampLights: THREE.PointLight[] = [];
  private pulseT = 0;
  private readonly forward = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly projected = new THREE.Vector3();
  private stopAlarm: (() => void) | null = null;

  constructor(
    scene: THREE.Object3D,
    private readonly audio: AudioManager,
  ) {
    scene.traverse((o) => {
      if (o.userData?.alarmLamp && (o as THREE.Mesh).isMesh) this.lamps.push(o as THREE.Mesh);
      if (o.userData?.alarmLight && (o as THREE.PointLight).isLight) {
        this.lampLights.push(o as THREE.PointLight);
      }
      if (o.userData?.interact !== 'alarm') return;
      const stand = o.userData.alarmStand as THREE.Vector3 | undefined;
      const knob = o.userData.alarmKnob as THREE.Mesh | undefined;
      if (!stand || !knob) return;
      this.buttons.push({ root: o, stand, knob });
    });
  }

  nearestStand(from: THREE.Vector3): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bestD = Infinity;
    let any: THREE.Vector3 | null = null;
    let anyD = Infinity;
    for (const b of this.buttons) {
      if (b.root.userData.destroyed) continue;
      const d = Math.hypot(b.stand.x - from.x, b.stand.z - from.z);
      if (d < anyD) {
        anyD = d;
        any = b.stand;
      }
      if (Math.abs(b.stand.y - from.y) > 2.6) continue;
      if (d < bestD) {
        bestD = d;
        best = b.stand;
      }
    }
    return best ?? any;
  }

  /** Returns true if F was used this frame. */
  update(player: PlayerController, input: Input, allowInteract = true, dt = 0): boolean {
    if (this.sounded) this.pulseLamps(dt);
    if (!allowInteract || !input.interactPressed) return false;
    const best = this.pick(player);
    if (!best) return false;
    this.sound(best.root.position);
    return true;
  }

  sound(at?: { x: number; y: number; z: number }): boolean {
    if (this.sounded) return false;
    this.sounded = true;
    const pos = at ?? this.buttons[0]?.root.position ?? { x: 0, y: 1.3, z: 0 };
    this.audio.playAt('switchClick', pos, undefined, { volume: 0.7, rate: 0.85, range: 28 });
    this.stopAlarm = this.audio.playAlarmOnce();
    for (const b of this.buttons) {
      if (!b.root.userData.destroyed) this.lightButton(b, true);
    }
    this.pulseLamps(0);
    this.onSounded?.();
    return true;
  }

  /** Hide a shot panel so it cannot be pulled. Returns the root if newly broken. */
  destroy(obj: THREE.Object3D): THREE.Object3D | null {
    let o: THREE.Object3D | null = obj;
    while (o && o.userData?.destructible !== 'alarm') o = o.parent;
    if (!o || o.userData.destroyed) return null;
    o.userData.destroyed = true;
    o.userData.ignoreHit = true;
    o.userData.interact = undefined;
    return o;
  }

  lookTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): { label: string; x: number; y: number } | null {
    const b = this.pick(player);
    if (!b || width < 8 || height < 8) return null;
    b.root.getWorldPosition(this.projected);
    this.projected.y += 0.12;
    this.projected.project(camera);
    if (this.projected.z < -1 || this.projected.z > 1) return null;
    return {
      label: this.sounded ? 'Alarm sounded' : 'F - Sound alarm',
      x: Math.min(width - 40, Math.max(40, (this.projected.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.projected.y * 0.5 + 0.5) * height)),
    };
  }

  dispose(): void {
    this.stopAlarm?.();
    this.stopAlarm = null;
  }

  private pulseLamps(dt: number): void {
    this.pulseT += dt;
    const k = 0.42 + 0.58 * (0.5 + 0.5 * Math.sin(this.pulseT * 5.2));
    for (const lamp of this.lamps) {
      if (lamp.userData.destroyed || !lamp.visible) continue;
      const root = lamp.parent;
      if (root && (root.userData.destroyed || !root.visible)) continue;
      const mat = lamp.material as THREE.MeshStandardMaterial;
      mat.emissive.setHex(0xff2208);
      mat.emissiveIntensity = 0.35 + 2.1 * k;
      mat.color.setHex(0xff4028);
    }
    for (const light of this.lampLights) {
      const root = light.parent;
      if (root && (root.userData.destroyed || !root.visible)) {
        light.intensity = 0;
        continue;
      }
      light.intensity = 1.15 * k;
    }
  }

  private lightButton(b: AlarmButton, on: boolean): void {
    const mat = b.knob.material as THREE.MeshStandardMaterial;
    mat.emissive.setHex(on ? 0xff2208 : 0x3a0404);
    mat.emissiveIntensity = on ? 1.6 : 0.25;
    mat.color.setHex(on ? 0xff4020 : 0xa01810);
  }

  private pick(player: PlayerController): AlarmButton | null {
    const pos = player.body.position;
    this.forward.set(-Math.sin(player.yaw), 0, -Math.cos(player.yaw));
    let best: AlarmButton | null = null;
    let bestScore = Infinity;
    for (const b of this.buttons) {
      if (b.root.userData.destroyed) continue;
      b.root.getWorldPosition(this.to);
      if (Math.abs(this.to.y - (pos.y + 1.2)) > 1.8) continue;
      this.to.y = pos.y;
      const dist = this.to.distanceTo(pos);
      if (dist > INTERACT_DIST) continue;
      this.to.sub(pos);
      if (this.to.lengthSq() > 1e-6) this.to.normalize();
      const facing = this.forward.dot(this.to);
      if (facing < FACING_DOT && dist > 0.95) continue;
      const score = dist - facing * 0.35;
      if (score < bestScore) {
        bestScore = score;
        best = b;
      }
    }
    return best;
  }
}
